<script setup>
// CRIAR / EDITAR FILA (telefonia, etapa E — 25/09/2026). Quem toca, em que
// ordem (a ordem só importa no rodízio), quanto toca em cada um, quanto o
// cliente espera no total e para onde vai se ninguém atender.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { iniciaisDoNome, corDoNome } from 'dashboard/helper/avatar';
import { opcoesDeDestino, nomeDoRamal } from './destinos';
import AudioEnviado from './AudioEnviado.vue';

const props = defineProps({
  fila: { type: Object, default: null },
  contexto: { type: Object, required: true },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});
const emit = defineEmits(['close', 'salvar']);

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO.FILA';
const editando = computed(() => Boolean(props.fila));

const nome = ref(props.fila?.nome || '');
const estrategia = ref(props.fila?.estrategia || 'ringall');
const toque = ref(props.fila?.toque ?? 20);
const espera = ref(props.fila?.espera ?? 120);
const senao = ref(props.fila?.senao || '');
const escolhidos = ref([...(props.fila?.ramais || [])]);
const descanso = ref(props.fila?.descanso ?? 0);
const musica = ref(Boolean(props.fila?.musica));
const mensagem = ref(null);
const tirarMensagem = ref(false);
const tentou = ref(false);

const ESTRATEGIAS = [
  { valor: 'ringall', icone: 'i-ph-bell-simple-ringing-fill' },
  { valor: 'rrmemory', icone: 'i-ph-arrows-clockwise-bold' },
  { valor: 'leastrecent', icone: 'i-ph-hourglass-medium-fill' },
];

const ramaisLigados = computed(() =>
  props.contexto.ramais.filter(r => r.ativo)
);
const ramalPorId = id => props.contexto.ramais.find(r => r.id === id);
const naFila = computed(() => escolhidos.value.map(ramalPorId).filter(Boolean));
const foraDaFila = computed(() =>
  ramaisLigados.value.filter(r => !escolhidos.value.includes(r.id))
);

const alternar = r => {
  const i = escolhidos.value.indexOf(r.id);
  if (i >= 0) escolhidos.value.splice(i, 1);
  else escolhidos.value.push(r.id);
};
const mover = (i, passo) => {
  const j = i + passo;
  if (j < 0 || j >= escolhidos.value.length) return;
  const lista = [...escolhidos.value];
  [lista[i], lista[j]] = [lista[j], lista[i]];
  escolhidos.value = lista;
};

const opcoesDoSenao = computed(() =>
  opcoesDeDestino(props.contexto, t, {
    excluir: props.fila ? `fila:${props.fila.id}` : '',
    comVazio: t('TELEFONIA.ROTEAMENTO.DESTINO.NENHUM'),
  })
);

const erroDoNome = computed(() =>
  nome.value.trim() ? '' : t(`${T}.NOME_FALTA`)
);

function salvar() {
  tentou.value = true;
  if (erroDoNome.value) return;
  // FormData: pode levar a mensagem de entrada (arquivo)
  const dados = new FormData();
  dados.append('name', nome.value.trim());
  dados.append('strategy', estrategia.value);
  dados.append('ring_seconds', String(toque.value));
  dados.append('max_wait_seconds', String(espera.value));
  dados.append('wrapup_seconds', String(descanso.value || 0));
  dados.append('hold_music', String(musica.value));
  dados.append('fallback_destination', senao.value || '');
  dados.append('ramais', escolhidos.value.join(','));
  if (mensagem.value) dados.append('greeting', mensagem.value);
  if (tirarMensagem.value) dados.append('remover_greeting', 'true');
  emit('salvar', dados);
}
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div v-modal-acessivel="() => emit('close')" class="advup-modal tel-fila">
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-users-three-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">
            {{
              editando ? t(`${T}.EDITAR`, { nome: fila.nome }) : t(`${T}.NOVA`)
            }}
          </h3>
          <p class="advup-modal__sub">{{ t(`${T}.SUB`) }}</p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t('TELEFONIA.ROTEAMENTO.CANCELAR')"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <form class="advup-modal__corpo tel-fila__corpo" @submit.prevent="salvar">
        <label class="tel-fila__campo">
          <span class="advup-rotulo">{{ t(`${T}.NOME`) }}</span>
          <input
            v-model="nome"
            name="nome-da-fila"
            type="text"
            maxlength="60"
            autocomplete="off"
            class="advup-campo reset-base"
            :placeholder="t(`${T}.NOME_PH`)"
            :aria-invalid="Boolean(tentou && erroDoNome)"
          />
          <span v-if="tentou && erroDoNome" class="advup-nota advup-nota--erro">
            {{ erroDoNome }}
          </span>
        </label>

        <!-- Como toca: três cartões, o escolhido em destaque -->
        <fieldset class="tel-fila__campo">
          <legend class="advup-rotulo">{{ t(`${T}.ESTRATEGIA`) }}</legend>
          <div class="tel-fila__modos" role="radiogroup">
            <button
              v-for="e in ESTRATEGIAS"
              :key="e.valor"
              type="button"
              role="radio"
              class="tel-fila__modo"
              :class="{ 'tel-fila__modo--sim': estrategia === e.valor }"
              :aria-checked="String(estrategia === e.valor)"
              @click="estrategia = e.valor"
            >
              <i :class="e.icone" aria-hidden="true" />
              <span class="tel-fila__modo-nome">
                {{ t(`${T}.ESTRATEGIAS.${e.valor}.NOME`) }}
              </span>
              <span class="tel-fila__modo-dica">
                {{ t(`${T}.ESTRATEGIAS.${e.valor}.DICA`) }}
              </span>
            </button>
          </div>
        </fieldset>

        <!-- Quem toca, na ordem -->
        <div class="tel-fila__campo">
          <span class="advup-rotulo">{{ t(`${T}.RAMAIS`) }}</span>
          <span class="advup-nota">
            {{
              estrategia === 'rrmemory'
                ? t(`${T}.RAMAIS_DICA_ORDEM`)
                : t(`${T}.RAMAIS_DICA`)
            }}
          </span>
          <TransitionGroup
            tag="ol"
            name="tel-fila-lista"
            class="tel-fila__lista"
          >
            <li v-for="(r, i) in naFila" :key="r.id" class="tel-fila__membro">
              <span class="tel-fila__pos">{{ i + 1 }}</span>
              <span
                class="advup-avatar advup-avatar--p"
                :class="`advup-avatar--${corDoNome(nomeDoRamal(r, t))}`"
                aria-hidden="true"
              >
                {{ iniciaisDoNome(nomeDoRamal(r, t)) }}
              </span>
              <span class="tel-fila__quem">
                <strong>{{ r.numero }}</strong> · {{ nomeDoRamal(r, t) }}
              </span>
              <button
                type="button"
                class="advup-acao"
                :disabled="i === 0"
                :aria-label="t(`${T}.SUBIR`, { numero: r.numero })"
                @click="mover(i, -1)"
              >
                <i class="i-ph-caret-up-bold" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="advup-acao"
                :disabled="i === naFila.length - 1"
                :aria-label="t(`${T}.DESCER`, { numero: r.numero })"
                @click="mover(i, 1)"
              >
                <i class="i-ph-caret-down-bold" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="advup-acao advup-acao--perigo"
                :aria-label="t(`${T}.TIRAR`, { numero: r.numero })"
                @click="alternar(r)"
              >
                <i class="i-hugeicons-cancel-01" aria-hidden="true" />
              </button>
            </li>
          </TransitionGroup>
          <p v-if="!naFila.length" class="advup-nota advup-nota--erro">
            {{ t(`${T}.NINGUEM`) }}
          </p>
          <div v-if="foraDaFila.length" class="tel-fila__disponiveis">
            <button
              v-for="r in foraDaFila"
              :key="r.id"
              type="button"
              class="tel-fila__chip"
              @click="alternar(r)"
            >
              <i class="i-ph-plus-bold" aria-hidden="true" />
              {{ r.numero }} · {{ nomeDoRamal(r, t) }}
            </button>
          </div>
        </div>

        <div class="tel-fila__duplo">
          <label class="tel-fila__campo">
            <span class="advup-rotulo">{{ t(`${T}.TOQUE`) }}</span>
            <span class="tel-fila__num">
              <input
                v-model.number="toque"
                name="toque"
                type="number"
                min="5"
                max="120"
                class="advup-campo reset-base"
              />
              <span>{{ t('TELEFONIA.ROTEAMENTO.SEGUNDOS') }}</span>
            </span>
            <span class="advup-nota">{{ t(`${T}.TOQUE_DICA`) }}</span>
          </label>
          <label class="tel-fila__campo">
            <span class="advup-rotulo">{{ t(`${T}.ESPERA`) }}</span>
            <span class="tel-fila__num">
              <input
                v-model.number="espera"
                name="espera"
                type="number"
                min="10"
                max="1800"
                class="advup-campo reset-base"
              />
              <span>{{ t('TELEFONIA.ROTEAMENTO.SEGUNDOS') }}</span>
            </span>
            <span class="advup-nota">{{ t(`${T}.ESPERA_DICA`) }}</span>
          </label>
        </div>

        <!-- O que o cliente ouve enquanto espera -->
        <AudioEnviado
          v-model:arquivo="mensagem"
          v-model:tirar="tirarMensagem"
          :atual="fila?.mensagem || null"
          :rotulo="t(`${T}.MENSAGEM`)"
          :dica="t(`${T}.MENSAGEM_DICA`)"
        />
        <button
          type="button"
          role="switch"
          class="advup-chave"
          :aria-checked="String(musica)"
          @click="musica = !musica"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.MUSICA`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.MUSICA_DICA`) }}</span>
          </span>
        </button>
        <label class="tel-fila__campo">
          <span class="advup-rotulo">{{ t(`${T}.DESCANSO`) }}</span>
          <span class="tel-fila__num">
            <input
              v-model.number="descanso"
              name="descanso"
              type="number"
              min="0"
              max="300"
              class="advup-campo reset-base"
            />
            <span>{{ t('TELEFONIA.ROTEAMENTO.SEGUNDOS') }}</span>
          </span>
          <span class="advup-nota">{{ t(`${T}.DESCANSO_DICA`) }}</span>
        </label>

        <div class="tel-fila__campo">
          <span class="advup-rotulo">{{ t(`${T}.SENAO`) }}</span>
          <AdvupSelect
            v-model="senao"
            :options="opcoesDoSenao"
            :rotulo="t(`${T}.SENAO`)"
            min-width="100%"
          />
          <span class="advup-nota">{{ t(`${T}.SENAO_DICA`) }}</span>
        </div>

        <p v-if="erro" class="tel-fila__erro" role="alert">
          <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
          <span>{{ erro }}</span>
        </p>
      </form>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t('TELEFONIA.ROTEAMENTO.CANCELAR') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'
            "
            aria-hidden="true"
          />
          {{
            salvando
              ? t('TELEFONIA.ROTEAMENTO.SALVANDO')
              : editando
                ? t('TELEFONIA.ROTEAMENTO.SALVAR')
                : t('TELEFONIA.ROTEAMENTO.CRIAR')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-fila__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}
.tel-fila__campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.tel-fila__modos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--esp-2);
}
.tel-fila__modo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--esp-1);
  padding: var(--esp-3);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background var(--mov-1) var(--mov-curva),
    transform var(--mov-1) var(--mov-curva);
}
.tel-fila__modo:hover {
  border-color: rgb(var(--teal-7));
}
.tel-fila__modo:active {
  transform: scale(0.98);
}
.tel-fila__modo > i {
  font-size: var(--ico-3);
  color: rgb(var(--slate-10));
  transition: color var(--mov-1) var(--mov-curva);
}
.tel-fila__modo--sim {
  border-color: rgb(var(--teal-8));
  background: radial-gradient(
      circle at 0 0,
      rgb(var(--teal-9) / 12%),
      transparent 70%
    ),
    rgb(var(--solid-1));
  box-shadow: 0 0 0 3px rgb(var(--teal-9) / 14%);
}
.tel-fila__modo--sim > i {
  color: rgb(var(--teal-10));
}
.tel-fila__modo-nome {
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
}
.tel-fila__modo-dica {
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}
.tel-fila__lista {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-fila__membro {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-2);
  border-radius: var(--r-3);
  background: rgb(var(--slate-2));
}
.tel-fila__pos {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}
.tel-fila__quem {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--txt-lista);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tel-fila__disponiveis {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
}
.tel-fila__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: var(--esp-1) var(--esp-3);
  border: 1px dashed rgb(var(--slate-6));
  border-radius: 999px;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
  cursor: pointer;
  touch-action: manipulation;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}
.tel-fila__chip:hover {
  border-color: rgb(var(--teal-8));
  color: rgb(var(--teal-11));
}
.tel-fila__duplo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-4);
}
.tel-fila__num {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-fila__num .advup-campo {
  width: 96px;
  font-variant-numeric: tabular-nums;
}
.tel-fila__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-12));
}
.tel-fila-lista-move,
.tel-fila-lista-enter-active,
.tel-fila-lista-leave-active {
  transition: all var(--mov-2) var(--mov-curva);
}
.tel-fila-lista-enter-from,
.tel-fila-lista-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}
.tel-fila-lista-leave-active {
  position: absolute;
}
@media (max-width: 639px) {
  .tel-fila__modos,
  .tel-fila__duplo {
    grid-template-columns: 1fr;
  }
}
</style>
